import { FC, useMemo } from 'react';
import { LoaderType, NextgenImage } from '../NextgenImage/NextgenImage';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import { Theme } from '../../types/enums';
import TextOnly, { TextOnlyTheme } from '../TextOnly/TextOnly';
import GenericLink from '../GenericLink/GenericLink';
import RawButton from '../Button/RawButton';
import { ButtonTheme, ButtonWidth } from '../Button/Button';
import styles from './ImageAndText.module.scss';
import { ImageAndTextProptypes } from './types/propsType';
import { ImageAndTextCtaBtnPosition, ImageAndTextDirection } from './types/enums';

const ImageAndText: FC<ImageAndTextProptypes> = (props: ImageAndTextProptypes) => {
  const {
    direction,
    theme = Theme.WHITE,
    className,
    containerBorderRadius,
    ctaConfig,
    button,
    imgPerViewport,
    textOnlyConfig,
    loaderType = LoaderType.CONTENTSTACK,
    ...restProps
  } = props;

  const viewport = useBreakpoint() as Breakpoints;
  const {
    src,
    alt,
    borderRadius,
    width = 100,
  } = useMemo(() => imgPerViewport[viewport || Breakpoints.MD], [viewport, imgPerViewport]);
  const containerClassName = useMemo(
    () =>
      `${styles.container} ${styles[direction ?? ImageAndTextDirection.LEFT_TEXT_RIGHT_IMAGE]} ${
        styles[theme]
      } ${className}`.trim(),
    [direction, theme],
  );

  return (
    <div className={styles['base-container']} data-testid="base-container-testid">
      <section
        className={containerClassName}
        style={{
          ...(containerBorderRadius ? { borderRadius: containerBorderRadius } : {}),
          ...(textOnlyConfig.backgroundColor ? { backgroundColor: textOnlyConfig.backgroundColor } : {}),
        }}
      >
        <div className={styles['body-text']}>
          <section className={styles['body-text-wrapper']}>
            <TextOnly className={styles['body-text-main']} theme={theme as TextOnlyTheme} {...textOnlyConfig} />
            {button?.title && ctaConfig?.href && (
              <GenericLink
                href={ctaConfig.href}
                target={ctaConfig.target}
                className={`${styles['cta-btn']} ${styles[button?.alignment ?? ImageAndTextCtaBtnPosition.LEFT]}`}
                data-testid="cta-btn-testid"
              >
                <RawButton
                  {...button}
                  theme={button.theme || (theme as ButtonTheme)}
                  buttonWidth={ButtonWidth.FIT_CONTENT}
                />
              </GenericLink>
            )}
          </section>
        </div>
        <div className={styles['body-image']}>
          <NextgenImage
            {...restProps}
            src={src}
            alt={alt}
            className={styles['img-main']}
            sizes="100vw"
            loading="lazy"
            width={0}
            height={0}
            fill={false}
            decoding="async"
            style={{
              height: 'auto',
              color: 'transparent',
              width: `${width ?? 100}%`,
              ...(borderRadius ? { borderRadius: borderRadius } : {}),
            }}
            loaderType={loaderType}
          />
        </div>
      </section>
    </div>
  );
};
export { ImageAndTextDirection, ImageAndTextCtaBtnPosition };
export type { ImageAndTextProptypes };
ImageAndText.displayName = 'ImageAndText';
export default ImageAndText;
